import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../../mock';

const BoldTestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section id="testimonials" className="py-32 px-6 relative bg-[#1A1430]/30">
      <div className="section-watermark" style={{ top: '5%', right: '-5%' }}>06</div>
      
      <div className="max-w-5xl mx-auto" ref={ref}>
        <motion.h2
          className="text-center mb-24 gradient-text"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          What clients say
        </motion.h2>
        
        {/* Bold Testimonial Card */}
        <div className="relative glass-card p-10 md:p-16">
          {/* Huge Gradient Quote Mark */}
          <motion.div
            className="absolute -top-8 -left-8"
            animate={{
              rotate: [0, 10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <Quote size={120} className="text-[#7C3AED] opacity-20" />
          </motion.div>
          
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.4 }
              }}
              className="relative z-10"
            >
              {/* Quote Text */}
              <p className="text-2xl md:text-3xl lg:text-4xl text-[#EDE9FE] leading-relaxed mb-12 italic font-light">
                "{currentTestimonial.text}"
              </p>
              
              {/* Client Info */}
              <div className="flex items-center gap-6 border-t-2 border-[#7C3AED]/20 pt-8">
                <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-[#7C3AED]/30">
                  <img
                    src={currentTestimonial.image}
                    alt={currentTestimonial.company}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                
                <div>
                  <p className="text-xl font-bold gradient-text" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
                    {currentTestimonial.author}
                  </p>
                  <p className="text-[#FF5C39] font-semibold text-lg">
                    {currentTestimonial.role}
                  </p>
                  <p className="text-[#9D97B5]">
                    {currentTestimonial.company}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          
          {/* Navigation Arrows */}
          <div className="flex gap-4 mt-12 justify-center">
            <motion.button
              onClick={prevSlide}
              className="p-4 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#C026D3] text-white hover:shadow-2xl hover:shadow-[#7C3AED]/50 transition-shadow"
              whileHover={{ scale: 1.1, rotate: -10 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={28} />
            </motion.button>
            <motion.button
              onClick={nextSlide}
              className="p-4 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#C026D3] text-white hover:shadow-2xl hover:shadow-[#7C3AED]/50 transition-shadow"
              whileHover={{ scale: 1.1, rotate: 10 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Next testimonial"
            >
              <ChevronRight size={28} />
            </motion.button>
          </div>
          
          {/* Gradient Progress Indicator */}
          <div className="flex gap-3 justify-center mt-8">
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className="h-2 rounded-full transition-all duration-300 overflow-hidden"
                animate={{
                  width: index === currentIndex ? 48 : 8,
                  opacity: index === currentIndex ? 1 : 0.4
                }}
                aria-label={`Go to testimonial ${index + 1}`}
              >
                <div className="h-full w-full bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#FF5C39]" />
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BoldTestimonialsSection;